import { PDFDocument, PDFPage, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import {
  BatteryDamageInfoData,
  BatteryImage,
  BatteryOverviewData,
} from '../../api/service/BatteryInterfaces';
import PretendardFont from '../../assets/fonts/PretendardVariable.ttf';
import PDFlogo from '../../assets/images/PDFlogo.png';
import PDFleaf from '../../assets/images/PDFleaf.png';
import ImageService from '../../api/service/image/ImageService';

// convertImageToPng 함수: 주어진 이미지 URL을 PNG Data URL로 변환합니다.
const convertImageToPng = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        // 'image/png' 포맷으로 변환한 Data URL 반환
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } else {
        reject(new Error('Canvas context is null'));
      }
    };
    img.onerror = (error) => {
      reject(error);
    };
  });
};

const generateDiagnosisTab2PDF = async (
  batteryDamageInfo: BatteryDamageInfoData,
  batteryOverview: BatteryOverviewData[],
  batteryImages: BatteryImage[]
) => {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);

  const fontBytes = await fetch(PretendardFont).then((res) =>
    res.arrayBuffer()
  );
  const pretendardFont = await pdfDoc.embedFont(fontBytes);

  const pageSize: [number, number] = [841.89, 595.28]; // A4 가로 크기
  let page: PDFPage = pdfDoc.addPage(pageSize);

  const { width, height } = page.getSize();
  const fontSize = 10;
  const titleFontSize = 16; // 타이틀 폰트 크기
  const margin = 10; // 좌우 여백
  let yPosition = height;

  // 타이틀 텍스트
  const titleHeight = 40; // 타이틀 영역 높이
  const title = '진단 보고서';
  const titleBgColor = rgb(0.137, 0.549, 0.392);
  const titleTextColor = rgb(1, 1, 1);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight,
    width: width,
    height: titleHeight,
    color: titleBgColor,
  });

  // 이미지 삽입
  const logoBytes = await fetch(PDFlogo).then((res) => res.arrayBuffer());
  const logoImage = await pdfDoc.embedPng(logoBytes);
  const logoDims = logoImage.scale(0.5);

  const logoX = margin;
  const logoY = yPosition - titleHeight + (titleHeight - logoDims.height) / 2;

  page.drawImage(logoImage, {
    x: logoX,
    y: logoY,
    width: logoDims.width,
    height: logoDims.height,
  });

  const titleX =
    width - margin - pretendardFont.widthOfTextAtSize(title, titleFontSize);
  const titleY = yPosition - titleHeight / 2 - titleFontSize / 2;

  page.drawText(title, {
    x: titleX,
    y: titleY,
    size: titleFontSize,
    font: pretendardFont,
    color: titleTextColor,
  });

  yPosition -= titleHeight + 10;

  const subTitleFontSize = 12; // 타이틀 폰트 크기
  const titleHeight2 = 20; // 타이틀 영역 높이
  const title2 = '기본정보'; // 타이틀 텍스트
  const titleTextColor2 = rgb(0, 0, 0);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight2,
    width: width,
    height: titleHeight2,
    color: rgb(1, 1, 1), // 원하는 배경색
  });

  // 이미지 삽입
  const imageBytes = await fetch(PDFleaf).then((res) => res.arrayBuffer());
  const image = await pdfDoc.embedPng(imageBytes);
  const imageDims = image.scale(0.5); // 이미지 크기 조정

  const imageX = margin; // 왼쪽 여백
  const imageY =
    yPosition - titleHeight2 + (titleHeight2 - imageDims.height) / 2; // 중앙 정렬

  page.drawImage(image, {
    x: imageX,
    y: imageY,
    width: imageDims.width,
    height: imageDims.height,
  });

  // 텍스트 왼쪽 정렬
  const textStartX = imageX + imageDims.width + 10; // 이미지 오른쪽 + 여백
  const textStartY = yPosition - titleHeight2 / 2 - subTitleFontSize / 2;

  page.drawText(title2, {
    x: textStartX,
    y: textStartY,
    size: subTitleFontSize,
    font: pretendardFont,
    color: titleTextColor2,
  });

  yPosition -= titleHeight2 + 10; // Y 위치 업데이트

  // 공통 열 너비 계산 함수
  const calculateDynamicColumnWidths1 = (
    pageWidth: number,
    margin: number,
    headers: string[],
    tableData: string[], // 1차원 배열
    font: typeof pretendardFont,
    fontSize: number
  ): number[] => {
    const availableWidth = pageWidth - margin * 2;
    const maxWidths = headers.map((header, i) =>
      Math.max(
        font.widthOfTextAtSize(header, fontSize),
        font.widthOfTextAtSize(tableData[i] || '-', fontSize) // tableData를 인덱스로 접근
      )
    );

    const totalWidth = maxWidths.reduce((sum, w) => sum + w, 0);
    const scaleFactor = availableWidth / totalWidth;

    return maxWidths.map((w) => w * scaleFactor);
  };

  const calculateDynamicColumnWidths = (
    pageWidth: number,
    margin: number,
    headers: string[],
    tableData: string[][],
    font: typeof pretendardFont,
    fontSize: number
  ): number[] => {
    const availableWidth = pageWidth - margin * 2;
    const maxWidths = headers.map((header, i) =>
      Math.max(
        font.widthOfTextAtSize(header, fontSize),
        ...tableData.map((row) => font.widthOfTextAtSize(row[i], fontSize))
      )
    );

    const totalWidth = maxWidths.reduce((sum, w) => sum + w, 0);
    const scaleFactor = availableWidth / totalWidth;

    return maxWidths.map((w) => w * scaleFactor);
  };

  // 헤더 그리기 함수
  const drawHeaderRow = (
    headers: string[],
    y: number,
    headerTextColor: ReturnType<typeof rgb>,
    headerBgColor: ReturnType<typeof rgb>,
    columnXPositions: number[],
    columnWidths: number[]
  ) => {
    page.drawRectangle({
      x: margin,
      y: y - fontSize - 7,
      width: width - margin * 2,
      height: fontSize + 8,
      color: headerBgColor,
    });

    headers.forEach((header, i) => {
      const textWidth = pretendardFont.widthOfTextAtSize(header, fontSize);
      const columnCenter = columnXPositions[i] + columnWidths[i] / 2;
      const textStartX = columnCenter - textWidth / 2;

      page.drawText(header, {
        x: textStartX,
        y: y - fontSize - 2,
        size: fontSize,
        font: pretendardFont,
        color: headerTextColor,
      });
    });
  };

  // batteryDamageInfo 출력
  // Header를 한글로 지정
  const infoHeaders = [
    '제조사',
    '차종',
    '차량번호',
    '제품번호',
    'ROM ID',
    'QR CODE',
    '생산일',
    '측정일',
  ];

  // 데이터에 값을 배열로 매칭
  const infoData = [
    batteryDamageInfo.info.manufacturer || '-',
    batteryDamageInfo.info.model || '-',
    batteryDamageInfo.info.carNo || '-',
    batteryDamageInfo.info.productNo || '-',
    batteryDamageInfo.info.romId || '-',
    batteryDamageInfo.info.qrcode || '-',
    batteryDamageInfo.info.productionDate || '-',
    batteryDamageInfo.info.measurementDate || '-',
  ];

  const infoColumnWidths = calculateDynamicColumnWidths1(
    width,
    margin,
    infoHeaders,
    infoData,
    pretendardFont,
    fontSize
  );

  const infoColumnXPositions = infoColumnWidths.reduce(
    (acc, w) => [...acc, acc[acc.length - 1] + w],
    [margin]
  );

  drawHeaderRow(
    infoHeaders,
    yPosition,
    rgb(1, 1, 1),
    rgb(0.569, 0.776, 0.698),
    infoColumnXPositions,
    infoColumnWidths
  );

  yPosition -= fontSize + 12;

  infoData.forEach((text, i) => {
    const safeValue = text || '-'; // undefined 방지
    const textWidth = pretendardFont.widthOfTextAtSize(safeValue, fontSize);
    const columnCenter = infoColumnXPositions[i] + infoColumnWidths[i] / 2;
    const textStartX = columnCenter - textWidth / 2;

    // 페이지 하단 도달 시 새 페이지 추가
    if (yPosition < margin + fontSize + 10) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - margin - 20;
    }

    // 텍스트 렌더링
    page.drawText(safeValue, {
      x: textStartX,
      y: yPosition - fontSize - 2,
      size: fontSize,
      font: pretendardFont,
      color: rgb(0, 0, 0), // 검정색 텍스트
    });
  });

  // 행 간격 추가
  yPosition -= fontSize + 10;
  // Header를 한글로 지정

  const titleHeight3 = 20; // 타이틀 영역 높이
  const title3 = '외부 손상상태 및 판매금액'; // 타이틀 텍스트
  const titleTextColor3 = rgb(0, 0, 0);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight3,
    width: width,
    height: titleHeight3,
    color: rgb(1, 1, 1), // 원하는 배경색
  });

  // 이미지 삽입
  const imageBytes2 = await fetch(PDFleaf).then((res) => res.arrayBuffer());
  const image2 = await pdfDoc.embedPng(imageBytes2);
  const imageDims2 = image2.scale(0.5); // 이미지 크기 조정

  const imageX2 = margin; // 왼쪽 여백
  const imageY2 =
    yPosition - titleHeight3 + (titleHeight3 - imageDims2.height) / 2; // 중앙 정렬

  page.drawImage(image2, {
    x: imageX2,
    y: imageY2,
    width: imageDims2.width,
    height: imageDims2.height,
  });

  // 텍스트 왼쪽 정렬
  const textStartX2 = imageX2 + imageDims2.width + 10; // 이미지 오른쪽 + 여백
  const textStartY2 = yPosition - titleHeight3 / 2 - subTitleFontSize / 2;

  page.drawText(title3, {
    x: textStartX2,
    y: textStartY2,
    size: subTitleFontSize,
    font: pretendardFont,
    color: titleTextColor3,
  });

  yPosition -= titleHeight3 + 10; // Y 위치 업데이트

  const damageHeaders = ['판매금액', '파손', '수분', '오염', '비고'];

  // 데이터에 값을 배열로 매칭
  const damageData = [
    new Intl.NumberFormat('ko-KR').format(batteryDamageInfo.damage.price) ||
      '-',
    batteryDamageInfo.damage.breakage || '-',
    batteryDamageInfo.damage.moisture || '-',
    batteryDamageInfo.damage.pollution || '-',
    batteryDamageInfo.damage.note || '-',
  ];

  const damageColumnWidths = calculateDynamicColumnWidths1(
    width,
    margin,
    damageHeaders,
    damageData,
    pretendardFont,
    fontSize
  );

  const damageColumnXPositions = damageColumnWidths.reduce(
    (acc, w) => [...acc, acc[acc.length - 1] + w],
    [margin]
  );

  drawHeaderRow(
    damageHeaders,
    yPosition,
    rgb(1, 1, 1),
    rgb(0.569, 0.776, 0.698),
    damageColumnXPositions,
    damageColumnWidths
  );

  yPosition -= fontSize + 12;

  damageData.forEach((text, i) => {
    const safeValue = text?.toString() || '-'; // 숫자를 문자열로 변환
    const textWidth = pretendardFont.widthOfTextAtSize(safeValue, fontSize);
    const columnCenter = damageColumnXPositions[i] + damageColumnWidths[i] / 2;
    const textStartX = columnCenter - textWidth / 2;

    // 페이지 하단 도달 시 새 페이지 추가
    if (yPosition < margin + fontSize + 10) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - margin - 20;
    }

    // 텍스트 렌더링
    page.drawText(safeValue, {
      x: textStartX,
      y: yPosition - fontSize - 2,
      size: fontSize,
      font: pretendardFont,
      color: rgb(0, 0, 0), // 검정색 텍스트
    });
  });

  // 행 간격 추가
  yPosition -= fontSize + 10;

  // batteryOverview 출력
  if (yPosition < margin + fontSize + 20) {
    page = pdfDoc.addPage(pageSize);
    yPosition = height - margin - 20;
  }

  const titleHeight4 = 20; // 타이틀 영역 높이
  const title4 = '측정결과'; // 타이틀 텍스트
  const titleTextColor4 = rgb(0, 0, 0);

  // 타이틀 배경
  page.drawRectangle({
    x: 0,
    y: yPosition - titleHeight4,
    width: width,
    height: titleHeight4,
    color: rgb(1, 1, 1), // 원하는 배경색
  });

  // 이미지 삽입
  const imageBytes3 = await fetch(PDFleaf).then((res) => res.arrayBuffer());
  const image3 = await pdfDoc.embedPng(imageBytes3);
  const imageDims3 = image3.scale(0.5); // 이미지 크기 조정

  const imageX3 = margin; // 왼쪽 여백
  const imageY3 =
    yPosition - titleHeight4 + (titleHeight4 - imageDims3.height) / 2; // 중앙 정렬

  page.drawImage(image3, {
    x: imageX3,
    y: imageY3,
    width: imageDims3.width,
    height: imageDims3.height,
  });

  // 텍스트 왼쪽 정렬
  const textStartX3 = imageX3 + imageDims3.width + 10; // 이미지 오른쪽 + 여백
  const textStartY3 = yPosition - titleHeight4 / 2 - subTitleFontSize / 2;

  page.drawText(title4, {
    x: textStartX3,
    y: textStartY3,
    size: subTitleFontSize,
    font: pretendardFont,
    color: titleTextColor4,
  });

  yPosition -= titleHeight4 + 10; // Y 위치 업데이트

  const overviewHeaders = ['NO', '이름', '상태', '결과', '단위'];
  const overviewData = batteryOverview.map((item) => [
    String(item.overviewNo || '-'),
    String(item.name || '-'),
    String(item.status || '-'),
    String(item.result || '-'),
    String(item.unit || '-'),
  ]);

  const overviewColumnWidths = calculateDynamicColumnWidths(
    width,
    margin,
    overviewHeaders,
    overviewData,
    pretendardFont,
    fontSize
  );

  const overviewColumnXPositions = overviewColumnWidths.reduce(
    (acc, w) => [...acc, acc[acc.length - 1] + w],
    [margin]
  );

  drawHeaderRow(
    overviewHeaders,
    yPosition,
    rgb(1, 1, 1),
    rgb(0.569, 0.776, 0.698),
    overviewColumnXPositions,
    overviewColumnWidths
  );

  yPosition -= fontSize + 12;

  overviewData.forEach((row) => {
    if (yPosition < margin + fontSize + 20) {
      page = pdfDoc.addPage(pageSize);
      yPosition = height - margin - 20;
    }

    row.forEach((text, i) => {
      const textWidth = pretendardFont.widthOfTextAtSize(text, fontSize);
      const columnCenter =
        overviewColumnXPositions[i] + overviewColumnWidths[i] / 2;
      const textStartX = columnCenter - textWidth / 2;

      page.drawText(text, {
        x: textStartX,
        y: yPosition - fontSize - 2,
        size: fontSize,
        font: pretendardFont,
        color: rgb(0, 0, 0),
      });
    });
    yPosition -= fontSize + 10;
  });

  // 새 페이지 추가
  const imagePage = pdfDoc.addPage(pageSize);
  const { width: imagePageWidth, height: imagePageHeight } =
    imagePage.getSize();

  // 이미지 페이지 타이틀
  const imageTitleHeight = 40; // 타이틀 영역 높이
  const imageTitle = '전체 이미지';
  const imageTitleBgColor = rgb(0.137, 0.549, 0.392);
  const imageTitleTextColor = rgb(1, 1, 1);

  // 타이틀 배경
  imagePage.drawRectangle({
    x: 0,
    y: imagePageHeight - imageTitleHeight,
    width: imagePageWidth,
    height: imageTitleHeight,
    color: imageTitleBgColor,
  });

  // 로고 이미지 삽입
  const logoBytes2 = await fetch(PDFlogo).then((res) => res.arrayBuffer());
  const logoImage2 = await pdfDoc.embedPng(logoBytes2);
  const logoDims2 = logoImage2.scale(0.5);

  const logoX2 = margin;
  const logoY2 =
    imagePageHeight -
    imageTitleHeight +
    (imageTitleHeight - logoDims2.height) / 2;

  imagePage.drawImage(logoImage2, {
    x: logoX2,
    y: logoY2,
    width: logoDims2.width,
    height: logoDims2.height,
  });

  const titleX2 =
    imagePageWidth -
    margin -
    pretendardFont.widthOfTextAtSize(imageTitle, titleFontSize);
  const titleY2 = imagePageHeight - imageTitleHeight / 2 - titleFontSize / 2;

  imagePage.drawText(imageTitle, {
    x: titleX2,
    y: titleY2,
    size: titleFontSize,
    font: pretendardFont,
    color: imageTitleTextColor,
  });

  const extractEncryptedId = (url: string): string => {
    const parts = url.split('/'); // 슬래시로 분리
    const indexOfImages = parts.indexOf('images'); // "images"의 위치를 찾음
    if (indexOfImages === -1) {
      throw new Error('URL에서 "images" 경로를 찾을 수 없습니다.');
    }
    return parts.slice(indexOfImages + 1).join('/'); // "images" 이후의 모든 경로를 조합
  };

  // 이미지 그룹 정의
  const imageGroup = await Promise.all(
    batteryImages.map(async (image) => {
      const imageUrl = await ImageService.getImageResource(
        extractEncryptedId(image.info.originalUrl)
      ); // URL 가져오기
      return {
        src: imageUrl, // ImageService로 가져온 URL
        scale: 0.5,
        originalWidth: image.info.originalWidth,
        originalHeight: image.info.originalHeight,
      };
    })
  );
  // 이미지 배치
  const imageMargin = 20; // 이미지 간 간격
  const fixedHeight = 60; // 고정된 이미지 높이
  // const fixedWidth = (imagePageWidth - imageMargin * 6) / 5; // 페이지 너비의 5분의 1 (간격 포함)
  let xPos = imageMargin; // 초기 x 좌표
  let yPos = imagePageHeight - imageTitleHeight - imageMargin; // 초기 y 좌표

  for (const imageInfo of imageGroup) {
    let imageSrc = imageInfo.src;

    // 만약 이미지 URL이 PNG 파일이 아니라면 PNG로 변환합니다.
    if (!imageSrc.toLowerCase().endsWith('.png')) {
      try {
        imageSrc = await convertImageToPng(imageSrc);
      } catch (error) {
        console.error('이미지 변환 오류:', error);
        continue; // 변환에 실패하면 해당 이미지는 건너뜁니다.
      }
    }

    const imageBytes = await fetch(imageSrc).then((res) => res.arrayBuffer());
    const embeddedImage = await pdfDoc.embedPng(imageBytes);

    // 고정된 너비에 맞춘 높이 계산 (비율 유지)
    const scaleFactor = fixedHeight / embeddedImage.height;
    const scaledWidth = embeddedImage.width * scaleFactor; // 비율 유지한 너비
    const scaledHeight = fixedHeight; // 고정된 높이

    // 새로운 행으로 이동
    if (xPos + scaledWidth > imagePageWidth - imageMargin) {
      xPos = imageMargin; // X 위치 초기화
      yPos -= scaledHeight + imageMargin; // Y 위치를 다음 행으로 이동

      // 페이지가 초과되면 새로운 페이지 추가
      if (yPos < imageMargin + scaledHeight) {
        page = pdfDoc.addPage(pageSize);
        xPos = imageMargin; // X 위치 초기화
        yPos = imagePageHeight - imageMargin - scaledHeight; // 새로운 페이지의 Y 초기화
      }
    }

    // 이미지 배치
    imagePage.drawImage(embeddedImage, {
      x: xPos,
      y: yPos - scaledHeight,
      width: scaledWidth,
      height: scaledHeight,
    });

    xPos += scaledWidth + imageMargin; // 다음 이미지로 이동
  }

  // PDF 저장
  const pdfBytes = await pdfDoc.save();
  return pdfBytes;
};

export default generateDiagnosisTab2PDF;
